import type { TFunction } from 'i18next';
import { calendarDayOfUtcMs } from '@lib/calendar-day';
import { ANALYTICS_PERIOD_KIND, type AnalyticsReportSummary } from './analyticsTypes';

/**
 * Readable names for analytics reports.
 *
 * Report periods are calendar days (`YYYY-MM-DD`) with no time zone. Every
 * computation here runs on UTC midnights and every formatter pins
 * `timeZone: 'UTC'`, so the day printed is the day stored — `new Date(day)`
 * formatted in a US time zone would print the day before.
 */

const DAY_MS = 86_400_000;

export interface DayRange {
  start: string;
  end: string;
}

type ReportPeriod = Pick<AnalyticsReportSummary, 'period_kind' | 'period_start' | 'period_end'>;

function parts(day: string): [number, number, number] {
  const [year = Number.NaN, month = Number.NaN, date = Number.NaN] = day.split('-').map(Number);
  return [year, month, date];
}

function utcMs(day: string): number {
  const [year, month, date] = parts(day);
  return Date.UTC(year, month - 1, date);
}

/** ISO-8601 week and week-year: the week belongs to the year of its Thursday. */
export function isoWeekOf(day: string): { year: number; week: number } {
  const ms = utcMs(day);
  const weekday = new Date(ms).getUTCDay() || 7;
  const thursday = new Date(ms + (4 - weekday) * DAY_MS);
  const year = thursday.getUTCFullYear();
  const week = Math.floor((thursday.getTime() - Date.UTC(year, 0, 1)) / DAY_MS / 7) + 1;
  return { year, week };
}

/** Days in the period, counting both ends. */
export function periodDays(start: string, end: string): number {
  return Math.round((utcMs(end) - utcMs(start)) / DAY_MS) + 1;
}

/**
 * The window `period_over_period_change` compares against: the same-length
 * span immediately before the period, as the backend metrics pack computes it
 * (`metrics-pack.ts` `buildGrowth`, #2530). The label must name what was compared.
 */
export function previousPeriodRange(start: string, end: string): DayRange {
  const shift = periodDays(start, end) * DAY_MS;
  return {
    start: calendarDayOfUtcMs(utcMs(start) - shift),
    end: calendarDayOfUtcMs(utcMs(end) - shift),
  };
}

function yearEarlier(day: string): string {
  const [year, month, date] = parts(day);
  // Clamp 29 February to the last day of the target month.
  const lastDay = new Date(Date.UTC(year - 1, month, 0)).getUTCDate();
  return calendarDayOfUtcMs(Date.UTC(year - 1, month - 1, Math.min(date, lastDay)));
}

/** The window `year_over_year_change` compares against. */
export function previousYearRange(start: string, end: string): DayRange {
  return { start: yearEarlier(start), end: yearEarlier(end) };
}

/**
 * `formatRange` is ES2021 Intl. Every supported browser ships it, but the
 * project's TypeScript `lib` stops at ES2020, so it is narrowed at run time.
 */
type RangeFormat = Intl.DateTimeFormat & { formatRange(start: Date, end: Date): string };

function hasFormatRange(format: Intl.DateTimeFormat): format is RangeFormat {
  return typeof Reflect.get(format, 'formatRange') === 'function';
}

/**
 * A locale-formatted day range. The year shows when the range crosses one, or
 * when the caller asks for it (a window from another year must say so).
 */
export function formatDateRange(
  start: string,
  end: string,
  locale: string | undefined,
  { withYear = false }: { withYear?: boolean } = {},
): string {
  const showYear = withYear || parts(start)[0] !== parts(end)[0];
  const format = new Intl.DateTimeFormat(locale, {
    ...(showYear ? { year: 'numeric' } : {}),
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
  const from = new Date(utcMs(start));
  const to = new Date(utcMs(end));
  return hasFormatRange(format)
    ? format.formatRange(from, to)
    : `${format.format(from)} – ${format.format(to)}`;
}

/** The report's readable name — weekly by ISO week, ad-hoc by length. Never its id. */
export function formatReportLabel(
  report: ReportPeriod,
  t: TFunction,
  locale: string | undefined,
): string {
  const range = formatDateRange(report.period_start, report.period_end, locale);
  if (report.period_kind === ANALYTICS_PERIOD_KIND.weekly) {
    const { year, week } = isoWeekOf(report.period_start);
    return t('analytics.report.weeklyLabel', { year, week, range });
  }
  return t('analytics.report.adhocLabel', {
    range,
    count: periodDays(report.period_start, report.period_end),
  });
}
